import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  FileText,
  LayoutGrid,
  List,
  LogOut,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useInstructorStore } from '@/stores/instructorStore';
// Removed WebSocket dependency - instructor dashboard doesn't need WebSocket
import { useUIStore } from '@/stores/uiStore';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { StatusIndicator } from '@/components/domain';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { cn } from '@/utils/cn';
import { useMotionSafe } from '@/utils/motion';
import type { StudentSessionInfo, AlertLevel } from '@/types';

const ALERT_PRIORITY: Record<AlertLevel, number> = {
  CRITICAL_ALERT: 0,
  WARNING: 1,
  DISTRACTED: 2,
  NORMAL: 3,
};

const InstructorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useUIStore();
  const { fadeRise, staggerContainer, staggerItem, hoverLift } = useMotionSafe();

  // Instructor store state
  const {
    studentSessions,
    selectedStudents,
    focusedStudent,
    isLoading,
    viewMode,
    filters,
    realTimeUpdates,
    dashboardStats,
    exportInProgress,
    lastRefresh,
    error: dashboardError,
    loadStudentSessions,
    refreshStudentData,
    selectStudent,
    focusOnStudent,
    setViewMode,
    setFilters,
    enableRealTimeUpdates,
    disableRealTimeUpdates,
    exportSelectedSessions,
    exportAllSessions,
    cleanupInactiveSessions,
    getFilteredStudents,
    getDashboardStats,
  } = useInstructorStore();

  // Honest live state: polling on + recent successful refresh (within 90s)
  const pollFresh =
    lastRefresh !== null && Date.now() - lastRefresh < 90_000;
  const isConnected = realTimeUpdates && pollFresh && !dashboardError;

  // Local state
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  // Initialize dashboard
  useEffect(() => {
    // Check authentication state once on mount
    const { isAuthenticated: currentAuth, sessionId: currentSessionId, user: currentUser } = useAuthStore.getState();
    
    if (!currentAuth || !currentSessionId || currentUser?.role !== 'teacher') {
      navigate('/login');
      return;
    }

    const initializeDashboard = async () => {
      try {
        // Load initial data (no WebSocket needed for instructor dashboard)
        await loadStudentSessions();
        getDashboardStats();
        
        // Enable real-time updates via polling
        if (realTimeUpdates) {
          enableRealTimeUpdates();
        }
      } catch (error) {
        console.error('Failed to initialize dashboard:', error);
        addNotification({
          type: 'error',
          title: 'Initialization Error',
          message: 'Failed to initialize instructor dashboard.',
        });
      }
    };

    initializeDashboard();

    // Set up periodic refresh
    const refreshInterval = setInterval(() => {
      if (realTimeUpdates) {
        refreshStudentData();
      }
    }, 30000); // Refresh every 30 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(refreshInterval);
      // No WebSocket to disconnect for instructor dashboard
    };
  }, [navigate, realTimeUpdates]); // Removed auth-related dependencies to prevent loops

  // Instructor dashboard uses polling instead of WebSocket for updates
  // Real-time updates are handled by the instructor store via periodic polling

  // Handle export operations
  const handleExportAll = useCallback(async () => {
    try {
      await exportAllSessions();
      addNotification({
        type: 'success',
        title: 'Export Complete',
        message: 'All sessions have been exported successfully.',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export session data.',
      });
    }
  }, [exportAllSessions, addNotification]);

  const handleExportSelected = useCallback(async () => {
    if (selectedStudents.size === 0) {
      addNotification({
        type: 'warning',
        title: 'No Selection',
        message: 'Please select at least one student session to export.',
      });
      return;
    }

    try {
      await exportSelectedSessions();
      addNotification({
        type: 'success',
        title: 'Export Complete',
        message: `${selectedStudents.size} session(s) exported successfully.`,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export selected sessions.',
      });
    }
  }, [selectedStudents.size, exportSelectedSessions, addNotification]);

  const handleCleanupSessions = useCallback(async () => {
    try {
      await cleanupInactiveSessions();
      addNotification({
        type: 'success',
        title: 'Cleanup Complete',
        message: 'Inactive sessions have been cleaned up.',
      });
      setShowCleanupModal(false);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Cleanup Failed',
        message: 'Failed to cleanup inactive sessions.',
      });
    }
  }, [cleanupInactiveSessions, addNotification]);

  const handleLogout = useCallback(() => {
    useAuthStore.getState().logout();
    navigate('/');
  }, [navigate]);

  // Helper functions
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'authenticated': return 'border-state-nominal/25 bg-state-nominal-soft text-state-nominal';
      case 'exam_completed': return 'border-state-neutral/25 bg-state-neutral-soft text-state-neutral';
      case 'in_progress': return 'border-state-nominal/25 bg-state-nominal-soft text-state-nominal';
      default: return 'border-state-neutral/25 bg-state-neutral-soft text-state-neutral';
    }
  };

  const getAlertLevelColor = (level: AlertLevel): string => {
    switch (level) {
      case 'NORMAL': return 'text-state-nominal';
      case 'WARNING': return 'text-state-warning';
      case 'CRITICAL_ALERT': return 'text-state-critical';
      default: return 'text-state-neutral';
    }
  };

  const formatTime = (timestamp: number | string): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const alertLevelLabel = (level: AlertLevel): string => {
    switch (level) {
      case 'CRITICAL_ALERT':
        return 'Critical';
      case 'WARNING':
        return 'Warning';
      case 'DISTRACTED':
        return 'Warning';
      default:
        return 'Nominal';
    }
  };

  const filteredStudents = [...getFilteredStudents()].sort((a, b) => {
    const aLevel = a.currentAlertLevel || 'NORMAL';
    const bLevel = b.currentAlertLevel || 'NORMAL';
    const byAlert = ALERT_PRIORITY[aLevel] - ALERT_PRIORITY[bLevel];
    if (byAlert !== 0) return byAlert;
    return (b.alertCount || 0) - (a.alertCount || 0);
  });

  const needsAttentionCount = filteredStudents.filter((s) => {
    const level = s.currentAlertLevel || 'NORMAL';
    return level === 'WARNING' || level === 'CRITICAL_ALERT';
  }).length;

  const recentAlerts = studentSessions
    .flatMap((session) => {
      const latest = session.monitoring?.[session.monitoring.length - 1];
      if (!latest) return [];
      if (latest.alertLevel !== 'WARNING' && latest.alertLevel !== 'CRITICAL_ALERT') {
        return [];
      }
      return [
        {
          id: `${session.sessionId}-${latest.timestamp}`,
          timestamp: latest.timestamp,
          studentName: session.studentName,
          alertLevel: latest.alertLevel,
          message:
            latest.message ||
            (latest.alertLevel === 'CRITICAL_ALERT'
              ? 'Critical attention alert'
              : 'Attention warning'),
        },
      ];
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 20);

  const lastPollLabel =
    lastRefresh === null
      ? 'Never'
      : `${Math.max(0, Math.round((Date.now() - lastRefresh) / 1000))}s ago`;

  if (isLoading && studentSessions.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas font-sans text-content-primary">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-hairline border-t-ink" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas font-sans text-content-primary">
      {/* Header */}
      <nav className="sticky top-0 z-50 border-b border-subtle bg-surface-canvas">
        <div className="mx-auto flex max-w-page flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <BrandMark />
            <div>
              <h1 className="font-display text-heading-sm font-normal">
                QuizSecure
              </h1>
              <p className="text-sm font-medium text-content-muted">Instructor Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between gap-3 md:justify-end">
            <StatusIndicator
              status={isConnected ? 'nominal' : realTimeUpdates ? 'warning' : 'offline'}
              label={
                isConnected
                  ? 'Nominal'
                  : realTimeUpdates
                    ? 'Stale'
                    : 'Offline'
              }
              pulse={isConnected}
            />
            <span className="hidden text-xs text-content-muted sm:inline">
              Last poll {lastPollLabel}
            </span>
            
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="size-4" aria-hidden="true" />
              Sign Out
            </Button>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-page px-4 py-8 sm:px-6 sm:py-10">
        {/* Integrity summary — alert-first */}
        <section aria-labelledby="overview-heading" className="mb-10">
          <h2 id="overview-heading" className="mb-4 font-display text-heading-sm font-normal">
            Integrity summary
          </h2>
          <motion.div
            className="grid grid-cols-2 overflow-hidden rounded-card border border-hairline bg-surface-card sm:grid-cols-4"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={staggerItem} className="border-b border-r border-subtle p-4 sm:border-b-0 sm:p-5">
              <div className="mb-1 text-2xl font-580 tabular-nums sm:text-3xl">
                {dashboardStats.totalStudents}
              </div>
              <div className="text-xs font-medium text-content-muted sm:text-sm">Active sessions</div>
            </motion.div>

            <motion.div variants={staggerItem} className="border-b border-subtle p-4 sm:border-b-0 sm:border-r sm:p-5">
              <div className="mb-1 text-2xl font-580 tabular-nums text-state-critical sm:text-3xl">
                {needsAttentionCount}
              </div>
              <div className="text-xs font-medium text-content-muted sm:text-sm">Needs attention</div>
            </motion.div>

            <motion.div variants={staggerItem} className="border-r border-subtle p-4 sm:p-5">
              <div className="mb-1 text-2xl font-580 tabular-nums sm:text-3xl">
                {(dashboardStats.alertsByLevel?.WARNING || 0) +
                  (dashboardStats.alertsByLevel?.CRITICAL_ALERT || 0)}
              </div>
              <div className="text-xs font-medium text-content-muted sm:text-sm">Open alerts</div>
            </motion.div>

            <motion.div variants={staggerItem} className="p-4 sm:p-5">
              <div className="mb-1 text-2xl font-580 tabular-nums sm:text-3xl">
                {dashboardStats.averageFocusPercentage.toFixed(1)}%
              </div>
              <div className="text-xs font-medium text-content-muted sm:text-sm">Avg focus</div>
            </motion.div>
          </motion.div>
        </section>

        {/* Active Exam Sessions Panel */}
        <section className="mb-10">
          <motion.div {...fadeRise}>
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-control bg-stone text-content-secondary">
                    <BarChart3 className="size-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="font-display text-heading-sm font-normal">
                      Active Exam Sessions
                    </h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <StatusIndicator
                        status={isConnected ? 'nominal' : 'offline'}
                        label={isConnected ? 'Nominal' : 'Offline'}
                        pulse={isConnected}
                      />
                      <span className="text-sm text-content-muted">
                        {needsAttentionCount > 0
                          ? `${needsAttentionCount} session(s) need attention`
                          : 'Alert-first session board'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {/* View Mode Toggle */}
                  <div className="grid grid-cols-2 rounded-control border border-hairline bg-surface-nested p-1" role="group" aria-label="Session view mode">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                      className={cn(
                        "inline-flex min-h-11 items-center justify-center rounded-control px-4 py-2 font-ui text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink",
                        viewMode === 'grid' 
                          ? "bg-paper text-content-primary" 
                          : "text-content-muted hover:text-content-primary"
                      )}
                    >
                      <LayoutGrid className="mr-2 size-4" aria-hidden="true" />
                      Grid
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      aria-pressed={viewMode === 'list'}
                      className={cn(
                        "inline-flex min-h-11 items-center justify-center rounded-control px-4 py-2 font-ui text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink",
                        viewMode === 'list' 
                          ? "bg-paper text-content-primary" 
                          : "text-content-muted hover:text-content-primary"
                      )}
                    >
                      <List className="mr-2 size-4" aria-hidden="true" />
                      List
                    </button>
                  </div>
                  
                  <Button 
                    size="sm" 
                    onClick={refreshStudentData} 
                    disabled={isLoading}
                    variant="secondary"
                    className="w-full sm:w-auto"
                  >
                    <RefreshCw className="mr-2 size-4" aria-hidden="true" />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Filters — items-end aligns selects with the labeled search input */}
              <div className="mb-8 grid items-end gap-3 lg:grid-cols-[minmax(0,1fr)_12rem_12rem]">
                <Input
                  label="Search students"
                  type="search"
                  placeholder="Search by name…"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ searchTerm: e.target.value })}
                />
                
                <label className="block">
                  <span className="sr-only">Filter by alert level</span>
                  <select
                    aria-label="Filter by alert level"
                    className="min-h-11 w-full rounded-control border border-hairline bg-paper px-4 py-3 font-ui text-content-primary focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15"
                    value={filters.alertLevel}
                    onChange={(e) => setFilters({ alertLevel: e.target.value as AlertLevel | 'all' })}
                  >
                    <option value="all">All Alert Levels</option>
                    <option value="NORMAL">Nominal</option>
                    <option value="WARNING">Warning</option>
                    <option value="CRITICAL_ALERT">Critical</option>
                  </select>
                </label>
                
                <label className="block">
                  <span className="sr-only">Filter by exam status</span>
                  <select
                    aria-label="Filter by exam status"
                    className="min-h-11 w-full rounded-control border border-hairline bg-paper px-4 py-3 font-ui text-content-primary focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15"
                    value={filters.examStatus}
                    onChange={(e) => setFilters({ examStatus: e.target.value })}
                  >
                    <option value="all">All Exam Status</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="paused">Paused</option>
                  </select>
                </label>
              </div>

              {/* Sessions Display */}
              {filteredStudents.length === 0 ? (
                <div className="border-y border-subtle py-12 text-center text-content-muted">
                  <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-stone">
                    <FileText className="size-7 text-content-secondary" aria-hidden="true" />
                  </div>
                  <p className="mb-2 font-display text-lg font-normal text-content-primary">No active exam sessions</p>
                  <p className="text-sm">Sessions will appear here when students start exams</p>
                </div>
              ) : (
                <motion.div
                  className={cn(
                    "gap-4",
                    viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "space-y-3"
                  )}
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {filteredStudents.map((session: StudentSessionInfo) => (
                    <motion.div key={session.sessionId} variants={staggerItem} {...hoverLift}>
                    <Card
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedStudents.has(session.sessionId)}
                      aria-label={`Select ${session.studentName}'s exam session`}
                      className={cn(
                        "group cursor-pointer border-hairline transition-colors hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-parchment",
                        selectedStudents.has(session.sessionId) && "ring-1 ring-ink",
                        focusedStudent === session.sessionId && "border-ink bg-surface-nested"
                      )}
                      onClick={() => selectStudent(session.sessionId)}
                      onDoubleClick={() => focusOnStudent(session.sessionId)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectStudent(session.sessionId);
                        }
                      }}
                    >
                      <CardContent className="p-5 sm:p-6">
                        <div className="mb-5 flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="break-words font-display text-lg font-normal text-content-primary">
                              {session.studentName}
                            </h4>
                            <p className="mt-1 break-all text-xs font-medium tabular-nums text-content-muted">
                              Session: {session.sessionId?.substring(0, 8) || 'Unknown'}...
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-2">
                            <Badge className={cn(
                              "border",
                              getStatusColor(session.examStatus)
                            )}>
                              {session.examStatus}
                            </Badge>
                            {session.alertCount > 5 && (
                              <Badge variant="danger" size="sm">
                                <AlertTriangle className="mr-1 size-3" aria-hidden="true" />
                                High Alerts
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 divide-x divide-subtle border-y border-subtle">
                          <div className="min-w-0 py-3 pr-3">
                            <span className="text-caption font-medium uppercase tracking-wider text-content-muted">Focus</span>
                            <div className={cn(
                              "mt-1 text-base font-580 tabular-nums sm:text-lg",
                              session.focusPercentage > 80 ? "text-state-nominal" :
                              session.focusPercentage > 60 ? "text-state-warning" : "text-state-critical"
                            )}>
                              {session.focusPercentage.toFixed(1)}%
                            </div>
                          </div>
                          <div className="min-w-0 px-3 py-3">
                            <span className="text-caption font-medium uppercase tracking-wider text-content-muted">Alerts</span>
                            <div className={cn(
                              "mt-1 text-base font-580 tabular-nums sm:text-lg",
                              session.alertCount > 5
                                ? "text-state-critical"
                                : session.alertCount > 0
                                  ? "text-state-warning"
                                  : "text-content-primary"
                            )}>
                              {session.alertCount}
                            </div>
                          </div>
                          <div className="min-w-0 py-3 pl-3">
                            <span className="text-caption font-medium uppercase tracking-wider text-content-muted">Status</span>
                            <div className={cn(
                              "mt-1 break-words text-caption font-580 sm:text-xs",
                              getAlertLevelColor(session.currentAlertLevel || 'NORMAL')
                            )}>
                              {session.currentAlertLevel || 'NORMAL'}
                            </div>
                          </div>
                        </div>

                        {session.examScore !== undefined && (
                          <div className="mt-4 rounded-control bg-surface-nested p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-content-secondary">Final Score</span>
                              <span className="text-lg font-580 tabular-nums text-content-primary">{session.examScore}%</span>
                            </div>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="xs"
                          className="mt-4 w-full"
                          onClick={(event) => {
                            event.stopPropagation();
                            focusOnStudent(session.sessionId);
                          }}
                        >
                          Focus session
                        </Button>
                      </CardContent>
                    </Card>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </CardContent>
          </Card>
          </motion.div>
        </section>

        {/* Live Monitoring Section */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Live Activity Log */}
          <div className="min-w-0 lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-control bg-stone text-content-secondary">
                      <Activity className="size-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h2 className="font-display text-heading-sm font-normal">
                        Live Activity Log
                      </h2>
                      <p className="mt-1 text-sm text-content-muted">Student monitoring events</p>
                    </div>
                  </div>
                  <StatusIndicator
                    status={isConnected ? 'nominal' : 'offline'}
                    label={isConnected ? 'Polling' : 'Paused'}
                    pulse={false}
                  />
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="max-h-96 space-y-3 overflow-y-auto">
                  {recentAlerts.length === 0 ? (
                    <div className="py-12 text-center text-content-muted">
                      <p className="mb-2 font-display text-lg font-normal text-content-primary">No open alerts</p>
                      <p className="text-sm">Warning and critical events from student sessions appear here</p>
                    </div>
                  ) : (
                    recentAlerts.map((alert) => (
                      <div key={alert.id} className="flex items-start gap-3 rounded-control border border-subtle bg-surface-nested p-3">
                        <div className="flex-shrink-0">
                          <span
                            className={cn(
                              "mt-2 inline-block size-2 rounded-full",
                              alert.alertLevel === 'CRITICAL_ALERT'
                                ? 'bg-state-critical'
                                : alert.alertLevel === 'WARNING'
                                  ? 'bg-state-warning'
                                  : 'bg-state-nominal'
                            )}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <p className="truncate text-sm font-medium text-content-primary">
                              {alert.studentName}
                            </p>
                            <p className="shrink-0 text-xs tabular-nums text-content-muted">
                              {formatTime(alert.timestamp)}
                            </p>
                          </div>
                          <p className="text-sm text-content-secondary">{alert.message}</p>
                          <p className={cn(
                            "mt-1 text-xs font-medium",
                            getAlertLevelColor(alert.alertLevel)
                          )}>
                            {alertLevelLabel(alert.alertLevel)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Control Panel — single integrity + actions column */}
          <aside className="divide-y divide-subtle overflow-hidden rounded-card border border-hairline bg-surface-card">
            <section>
              <div className="px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
                <h3 className="font-display text-heading-sm font-normal">
                  Feed status
                </h3>
              </div>
              <div className="px-5 pb-5 sm:px-6 sm:pb-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content-secondary">Session feed</span>
                    <StatusIndicator
                      status={realTimeUpdates ? (pollFresh ? 'nominal' : 'warning') : 'offline'}
                      label={realTimeUpdates ? (pollFresh ? 'Nominal' : 'Stale') : 'Offline'}
                      pulse={false}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content-secondary">Last poll</span>
                    <span className="font-580 tabular-nums text-content-primary">
                      {lastPollLabel}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content-secondary">Needs attention</span>
                    <span className="font-580 tabular-nums text-content-primary">
                      {needsAttentionCount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-subtle pt-4">
                    <span className="text-sm text-content-secondary">Warning / critical</span>
                    <span className="font-580 tabular-nums text-content-primary">
                      {(dashboardStats.alertsByLevel?.WARNING || 0)} /{' '}
                      {(dashboardStats.alertsByLevel?.CRITICAL_ALERT || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
                <h3 className="font-display text-heading-sm font-normal">
                  Actions
                </h3>
              </div>
              <div className="px-5 pb-5 sm:px-6 sm:pb-6">
                <div className="space-y-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={handleExportAll}
                    loading={exportInProgress}
                  >
                    Export all sessions
                  </Button>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={handleExportSelected}
                    disabled={selectedStudents.size === 0 || exportInProgress}
                  >
                    Export selected ({selectedStudents.size})
                  </Button>
                  
                  <Button
                    variant="danger"
                    size="sm"
                    className="w-full"
                    onClick={() => setShowCleanupModal(true)}
                  >
                    Cleanup old sessions
                  </Button>

                  <Button
                    variant={realTimeUpdates ? "primary" : "secondary"}
                    size="sm"
                    className="w-full"
                    onClick={realTimeUpdates ? disableRealTimeUpdates : enableRealTimeUpdates}
                  >
                    {realTimeUpdates ? 'Pause updates' : 'Enable live updates'}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setFilters({ alertLevel: 'all', examStatus: 'all', searchTerm: '', focusThreshold: 0 })}
                  >
                    Reset filters
                  </Button>
                </div>
              </div>
            </section>
          </aside>
        </div>
      </main>

      {/* Cleanup Confirmation Modal */}
      <Modal
        isOpen={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        ariaLabel="Cleanup sessions confirmation"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full border border-state-critical/25 bg-state-critical-soft">
            <Trash2 className="size-7 text-state-critical" aria-hidden="true" />
          </div>
          <h3 className="mb-4 font-display text-heading-sm font-normal text-content-primary">Cleanup Sessions</h3>
          <p className="mb-6 text-content-secondary">
            Are you sure you want to cleanup old inactive sessions? This action cannot be undone.
          </p>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <Button variant="ghost" onClick={() => setShowCleanupModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleCleanupSessions}>
              Cleanup Sessions
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InstructorDashboard;